import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Nomenclature, OperationBlock, TKModel } from "../components/Data/Data";
import { useParams } from "react-router-dom";
import { fetchTKModel } from "../api/requests";
import { notifyError } from "../../../utils/toasts";

export const TKModelContext = createContext();

export const TKModelProvider = ({ children }) => {
  const { productSheetId } = useParams();

  const [tkModel, setTkModel] = useState(new TKModel());
  const [serverData, setServerData] = useState(null);
  const [finalNomenclature, setFinalNomenclautre] = useState(null);
  const [parentNomenclatureOperationId, setParentNomenclatureOperationId] =
    useState(null);

  const updateServerData = useCallback((fetchedData) => {
    setServerData(fetchedData);
  }, []);

  const addFinalNomenclature = useCallback((finalNomenclatureId) => {
    setFinalNomenclautre(finalNomenclatureId);
  }, []);

  const [selectedOperationId, setSelectedOperationId] = useState(null);
  const [selectedNomenclatureId, setSelectedNomenclatureId] = useState(null);

  const selectOperation = useCallback((operationId) => {
    setSelectedOperationId(operationId);
  }, []);

  const selectParentNomenclatureOperation = useCallback((operationId) => {
    setParentNomenclatureOperationId(operationId);
  }, []);

  const selectNomenclature = useCallback((nomenclatureId) => {
    setSelectedNomenclatureId(nomenclatureId);
  }, []);

  const selectedNomenclatureOperation = useMemo(() => {
    if (serverData?.operations) {
      // проверяем есть ли выделенная номенклатура или сбрасываем это значение
      if (parentNomenclatureOperationId && selectedNomenclatureId) {
        return serverData?.operations.find(
          ({ operation_id }) => operation_id === parentNomenclatureOperationId
        );
      }
    }
    return null;
  }, [
    parentNomenclatureOperationId,
    selectedNomenclatureId,
    serverData?.operations,
  ]);

  const selectedOperationData = useMemo(() => {
    if (serverData?.operations && !selectedNomenclatureOperation) {
      if (selectedOperationId) {
        return serverData.operations.find(
          ({ operation_id }) => operation_id === selectedOperationId
        );
      }

      // Если не найдена выделенная операция, то выбираем финальную
      return serverData.operations.find(
        ({ operation_id, final_operation_id }) =>
          operation_id === serverData?.final_operation_id
      );
    }
    return null;
  }, [
    selectedNomenclatureOperation,
    selectedOperationId,
    serverData?.final_operation_id,
    serverData?.operations,
  ]);

  useEffect(() => {
    try {
      if (productSheetId) {
        fetchTKModel(productSheetId, updateServerData);
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  }, [productSheetId, updateServerData]);

  useEffect(() => {
    if (serverData) {
      const nomenclatures = {};
      serverData.operations?.forEach((operation) => {
        operation.incoming_nomenclature.forEach((n) => {
          nomenclatures[n.nomenclature_id] = new Nomenclature({
            id: n.nomenclature_id,
            name: n.name,
            quantity: n.nomenclature_quantity,
            isInput: true,
            previousItems: operation.previous_operations,
            nextItems: operation.next_operations,
          });
        });

        operation.outgoing_nomenclature.forEach((n) => {
          nomenclatures[n.nomenclature_id] = new Nomenclature({
            id: n.nomenclature_id,
            name: n.name,
            quantity: n.quantity,
            isOutput: true,
            previousItems: operation.previous_operations,
            nextItems: operation.next_operations,
          });
        });
      });

      const operations = serverData.operations?.map((operation) => {
        return new OperationBlock({
          id: operation.operation_id,
          name: operation.name,
          time: operation.equipment_settings.reduce(
            (acc, ep) => acc + ep.execution_time,
            0
          ),
          incomingNomenclature: operation.incoming_nomenclature.map((n) => ({
            nomenclature_id: nomenclatures[n.nomenclature_id].id,
            nomenclature_name: nomenclatures[n.nomenclature_id].name,
            quantity: n.quantity,
            previous_items: operation.previous_operations,
            next_items: operation.next_operations,
          })),
          outgoingNomenclature: operation.outgoing_nomenclature.map((n) => ({
            nomenclature_id: nomenclatures[n.nomenclature_id].id,
            nomenclature_name: nomenclatures[n.nomenclature_id].name,
            quantity: n.quantity,
            previous_items: operation.previous_operations,
            next_items: operation.next_operations,
          })),
          additionalInfo: operation.equipment_settings.map(
            (ep) => ep.equipment_group_name
          ),
          nextOperations: operation.next_operations,
          previousOperations: operation.previous_operations,
          finalOperationId: serverData.final_operation_id,
          finalNomenclatureId: serverData.final_nomenclature_id,
        });
      });

      const newTkModel = new TKModel();
      operations?.forEach((operation) =>
        newTkModel.addOperationBlock(operation)
      );
      setTkModel(newTkModel);
    }
  }, [serverData]);

  return (
    <TKModelContext.Provider
      value={{
        tkModel,
        selectOperation,
        updateServerData,
        productSheetId,
        selectedOperationData,
        serverData,
        selectNomenclature,
        selectedNomenclatureId,
        finalNomenclature,
        addFinalNomenclature,
        selectParentNomenclatureOperation,
        selectedNomenclatureOperation,
      }}
    >
      {children}
    </TKModelContext.Provider>
  );
};
